import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Phone, Filter, RefreshCw, ChevronRight, Clock, AlertCircle, CheckCircle2,
  XCircle, Calendar as CalendarIcon, User,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'attempted', label: 'Attempted' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'awaiting_records', label: 'Awaiting Records' },
  { value: 'patient_declined', label: 'Declined' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_OPTIONS = [
  { value: 'all', label: 'All Priorities' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'important', label: 'Important' },
  { value: 'routine', label: 'Routine' },
];

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  attempted: 'bg-amber-100 text-amber-700',
  scheduled: 'bg-indigo-100 text-indigo-700',
  awaiting_records: 'bg-orange-100 text-orange-700',
  patient_declined: 'bg-red-100 text-red-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700 border-red-200',
  important: 'bg-amber-100 text-amber-700 border-amber-200',
  routine: 'bg-slate-100 text-slate-600 border-slate-200',
};

const QUICK_STATUSES = ['attempted', 'scheduled', 'awaiting_records', 'completed', 'patient_declined'];

function TaskRow({ task, onUpdate, onClose, patientName }) {
  const [expanding, setExpanding] = useState(false);
  const [notes, setNotes] = useState(task.notes || '');

  const handleQuickStatus = async (newStatus) => {
    if (['completed', 'patient_declined', 'cancelled'].includes(newStatus)) {
      await onClose(task.id, newStatus, notes);
    } else {
      await onUpdate(task.id, { status: newStatus, notes });
    }
  };

  const dueDate = task.due_date ? new Date(task.due_date) : null;
  const isOverdue = dueDate && dueDate < new Date() && !['completed', 'cancelled'].includes(task.status);

  return (
    <div className="border rounded-lg p-3 hover:bg-muted/30 transition-colors" data-testid={`outreach-task-${task.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.routine}`}>
              {task.priority}
            </span>
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${STATUS_COLORS[task.status] || 'bg-slate-100 text-slate-600'}`}>
              {task.status?.replace(/_/g, ' ')}
            </span>
            {isOverdue && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-50 text-red-600 flex items-center gap-0.5">
                <AlertCircle className="w-3 h-3" /> Overdue
              </span>
            )}
          </div>
          <h4 className="text-sm font-medium truncate">{task.title}</h4>
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><User className="w-3 h-3" /> {patientName || task.patient_id?.slice(0, 8)}</span>
            {task.measure_id && <span>{task.measure_id.replace('qm_', '').replace(/_/g, ' ')}</span>}
            {dueDate && <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> Due {dueDate.toLocaleDateString()}</span>}
          </div>
        </div>
        <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setExpanding(!expanding)} data-testid={`task-expand-${task.id}`}>
          <ChevronRight className={`w-4 h-4 transition-transform ${expanding ? 'rotate-90' : ''}`} />
        </Button>
      </div>

      {expanding && (
        <div className="mt-3 pt-3 border-t space-y-2">
          {task.description && <p className="text-xs text-muted-foreground">{task.description}</p>}
          <div className="flex items-center gap-2">
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes..."
              className="text-xs h-8 flex-1"
              data-testid={`task-notes-${task.id}`}
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_STATUSES.filter(s => s !== task.status).map(s => (
              <Button
                key={s}
                variant="outline"
                size="sm"
                className="text-[10px] h-7 px-2"
                onClick={() => handleQuickStatus(s)}
                data-testid={`task-status-${s}-${task.id}`}
              >
                {s === 'completed' ? <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" /> :
                 s === 'patient_declined' ? <XCircle className="w-3 h-3 mr-1 text-red-600" /> :
                 <Clock className="w-3 h-3 mr-1" />}
                {s.replace(/_/g, ' ')}
              </Button>
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground">
            Created {new Date(task.created_at).toLocaleDateString()} · Reason: {task.reason_code?.replace(/_/g, ' ')}
            {task.last_contact_at && ` · Last contact: ${new Date(task.last_contact_at).toLocaleDateString()}`}
          </div>
        </div>
      )}
    </div>
  );
}

export default function QualityOutreach() {
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [measureFilter, setMeasureFilter] = useState('');
  const [measures, setMeasures] = useState([]);
  const [patients, setPatients] = useState({});
  const [page, setPage] = useState(0);
  const limit = 25;

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (measureFilter) params.append('measure_id', measureFilter);
      params.append('limit', limit);
      params.append('offset', page * limit);
      const res = await api.get(`/quality-outreach/tasks?${params}`);
      const taskList = res.data.tasks || [];
      setTasks(taskList);
      setTotal(res.data.total || 0);

      // Resolve patient names
      const pIds = [...new Set(taskList.map(t => t.patient_id).filter(Boolean))];
      const ptMap = { ...patients };
      for (const pid of pIds) {
        if (!ptMap[pid]) {
          try {
            const pRes = await api.get(`/patients/${pid}`);
            const pt = pRes.data.patient || pRes.data;
            ptMap[pid] = pt?.name ? `${pt.name.first} ${pt.name.last}` : pid.slice(0, 8);
          } catch { ptMap[pid] = pid.slice(0, 8); }
        }
      }
      setPatients(ptMap);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter, priorityFilter, measureFilter, page]);

  useEffect(() => {
    api.get('/quality/dashboard/by-measure').then(res => {
      const m = res.data.by_measure || {};
      setMeasures(Object.entries(m).map(([id, data]) => ({ id, name: data.name || id })));
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleUpdate = async (taskId, updates) => {
    try {
      await api.put(`/quality-outreach/tasks/${taskId}`, updates);
      fetchTasks();
    } catch (err) { console.error(err); }
  };

  const handleClose = async (taskId, status, notes) => {
    try {
      await api.post(`/quality-outreach/tasks/${taskId}/close`, { status, notes });
      fetchTasks();
    } catch (err) { console.error(err); }
  };

  const activeCount = tasks.filter(t => !['completed', 'cancelled'].includes(t.status)).length;

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-4" data-testid="quality-outreach-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary" /> Outreach Queue
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {total} total · {activeCount} active on this page
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchTasks} data-testid="outreach-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="border">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-muted-foreground shrink-0" />
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="outreach-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={v => { setPriorityFilter(v); setPage(0); }}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="outreach-priority-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={measureFilter || 'all'} onValueChange={v => { setMeasureFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="outreach-measure-filter">
                <SelectValue placeholder="All Measures" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Measures</SelectItem>
                {measures.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Task List */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
        </div>
      ) : tasks.length === 0 ? (
        <Card className="border">
          <CardContent className="p-8 text-center">
            <Phone className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-40" />
            <p className="text-sm text-muted-foreground">No outreach tasks match your filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {tasks.map(task => (
            <TaskRow key={task.id} task={task} onUpdate={handleUpdate} onClose={handleClose} patientName={patients[task.patient_id]} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="outreach-prev">Prev</Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)} data-testid="outreach-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
